import { ButtonHTMLAttributes, ReactElement } from 'react';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import {
  colors,
  fontWeight,
  radius,
  s4,
  space,
  timeFastMs,
} from '@ardoq/design-tokens';
import { InlineText, text2 } from '@ardoq/typography';
import { getInsetFocusRingBorderCSS } from '@ardoq/style-helpers';
import styled, { css } from 'styled-components';
import { BadgeDot, BadgeDotSize, Tag } from '@ardoq/status-ui';
import { Icon, ICON_SELECTOR, IconName, IconSize } from '@ardoq/icons';
import { TabProps } from './types';
import {
  TAB_HOVER_HEIGHT,
  TAB_UNDERLINE_HEIGHT,
  TAB_UNDERLINE_SPACE,
} from './consts';
import {
  WithPopover,
  PlainTextPopover,
  WithUncontrolledPopover,
} from '@ardoq/popovers';

type NativeButtonProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'onBlur' | 'type' | 'autoFocus' | 'onFocus' | 'id'
>;

type TabButtonProps = NativeButtonProps & TabProps;

type InternalTabProps = TabButtonProps & {
  hasDragHandle?: boolean;
};

const buttonStyles = css`
  background: none;
  color: ${colors.textModerate};

  &::after {
    height: ${TAB_UNDERLINE_HEIGHT}px;
    background: ${colors.textDefault};
  }

  &:hover {
    background: ${colors.surfaceHover};
  }
`;

const activeStyles = css`
  && {
    color: ${colors.textDefault};
  }
`;

const TabButtonStyles = styled.button<{
  $isActive: boolean;
  $full?: boolean;
}>`
  ${text2};
  font-weight: ${fontWeight.semibold};
  display: flex;
  gap: ${space.small};
  flex-shrink: 0;
  align-items: center;

  ${({ $full }) =>
    $full &&
    css`
      flex: 1;
      justify-content: center;
    `}

  border: 0;
  cursor: pointer;
  height: ${TAB_HOVER_HEIGHT}px;
  margin-bottom: ${TAB_UNDERLINE_SPACE}px;
  padding: 0 ${space.small};
  border-radius: ${radius.medium};

  position: relative;
  &:focus-visible {
    ${getInsetFocusRingBorderCSS()}
  }

  &:disabled {
    pointer-events: none;
    color: ${colors.textDisabled};
  }

  &::after {
    transition: transform ${timeFastMs} ease;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    width: 100%;
    bottom: -${TAB_UNDERLINE_SPACE}px;
    content: ' ';
    position: absolute;
    display: block;
  }

  ${buttonStyles}

  ${({ $isActive }) =>
    $isActive &&
    css`
      ${activeStyles}
      &::after {
        transform: translateX(-50%) scaleX(1);
      }
    `}
`;

const DragHandleIcon = styled(Icon)`
  cursor: grab;
`;

type ChevronTabButtonStylesProps = {
  $isVisible: boolean;
  $direction: 'left' | 'right';
  onClick?: VoidFunction;
};

const chevronStyles = css`
  background: ${colors.surfaceDefault};

  && {
    border-color: ${colors.borderSubtle};
  }
`;

const ChevronTabButtonStyles = styled(
  TabButtonStyles
)<ChevronTabButtonStylesProps>`
  z-index: 1;
  position: absolute;
  height: 100%;
  padding: 0;
  border-radius: 0;
  margin: 0;
  transition: transform 0.2s;

  ${ICON_SELECTOR} {
    margin: 0 ${s4};
  }
  ${chevronStyles}

  ${({ $direction, $isVisible }) =>
    $direction === 'left'
      ? css`
          left: 0;
          border-right: 1px solid;
          transform: translateX(${$isVisible ? '0%' : '-100%'});
        `
      : css`
          right: 0;
          border-left: 1px solid;
          transform: translateX(${$isVisible ? '0%' : '100%'});
        `}
`;

type ChevronTabButtonProps = {
  isVisible: boolean;
  direction: 'left' | 'right';
  onClick?: VoidFunction;
};

export const ChevronTabButton = ({
  direction,
  isVisible,
  ...restProps
}: ChevronTabButtonProps) => {
  return (
    <ChevronTabButtonStyles
      $isActive={false}
      $direction={direction}
      disabled={!isVisible}
      $isVisible={isVisible}
      {...restProps}
    >
      {direction === 'left' && <Icon iconName={IconName.CHEVRON_LEFT} />}
      {direction === 'right' && <Icon iconName={IconName.CHEVRON_RIGHT} />}
    </ChevronTabButtonStyles>
  );
};

export const InternalTab = ({
  isDisabled,
  isActive,
  iconName,
  rightIconName,
  rightIconPopoverMessage,
  label,
  tag,
  tagStatusType,
  tagPopoverContent,
  statusType,
  badgeTooltipText,
  clickId,
  dataTourId,
  tabId,
  full,
  hasDragHandle,
  ...restButtonProps
}: InternalTabProps) => {
  return (
    <TabButtonStyles
      $full={full}
      disabled={isDisabled}
      $isActive={isActive || false}
      data-click-id={clickId}
      data-tour-id={dataTourId}
      data-tab-id={tabId}
      role="tab"
      aria-selected={isActive}
      {...restButtonProps}
    >
      {hasDragHandle && (
        <DragHandleIcon
          iconName={IconName.DRAG_INDICATOR}
          iconSize={IconSize.SMALL}
          color={isDisabled ? colors.iconActionDisabled : colors.icon}
        />
      )}
      {iconName && (
        <Icon
          iconSize={IconSize.SMALL}
          iconName={iconName}
          color={isDisabled ? colors.iconActionDisabled : colors.icon}
        />
      )}
      <InlineText>{label}</InlineText>
      {rightIconName && (
        <WithUncontrolledPopover
          content={
            rightIconPopoverMessage ? (
              <PlainTextPopover>{rightIconPopoverMessage}</PlainTextPopover>
            ) : null
          }
        >
          <Icon
            iconName={rightIconName}
            iconSize={IconSize.SMALL}
            color={isDisabled ? colors.iconActionDisabled : colors.icon}
          />
        </WithUncontrolledPopover>
      )}
      {tag && (
        <WithPopover content={tagPopoverContent}>
          <Tag label={`${tag}`} statusType={tagStatusType} />
        </WithPopover>
      )}
      {statusType && (
        <BadgeDot
          color={statusType}
          size={BadgeDotSize.SMALL}
          data-tooltip-text={badgeTooltipText}
        />
      )}
      {statusType && badgeTooltipText && (
        <VisuallyHidden elementType="span">{` ${badgeTooltipText}`}</VisuallyHidden>
      )}
    </TabButtonStyles>
  );
};

export const Tab: (props: TabButtonProps) => ReactElement = InternalTab;
